import { useCallback, useEffect, useMemo, useState } from 'react';

import { usePreviewVoucher } from '~/hooks/apis/promo/usePreviewVoucher';

import type {
  PreviewPublicVoucher,
  PreviewVoucherPayload,
  PreviewVoucherTextReplacement,
} from '~/services/PromoService';

import ApiError from '~/types/ApiError';

type VoucherPreviewPayloadBase = Omit<PreviewVoucherPayload, 'previewCurrencyCode' | 'previewExpiresAt' | 'locale'>;

type UseVoucherPreviewParams = {
  currencyOptions: Array<string>;
  defaultCurrencyCode: string;
  defaultLocale: string;
  buildPayload: () => VoucherPreviewPayloadBase | null;
  getMissingFieldMessages?: () => Array<string>;
};

export function getPreviewCurrencyOptions(currencyOptions: Array<string>, defaultCurrencyCode: string): Array<string> {
  const seen = new Set<string>();
  return [...currencyOptions, defaultCurrencyCode]
    .map((currency) => currency.trim().toUpperCase())
    .filter((currency) => {
      if (!currency || seen.has(currency)) {
        return false;
      }
      seen.add(currency);
      return true;
    });
}

function formatApiErrorDetails(errors: unknown): string {
  if (errors == null || errors === '') {
    return '';
  }
  if (typeof errors === 'string') {
    return errors.trim();
  }
  if (Array.isArray(errors)) {
    return errors
      .map((item) => {
        if (typeof item === 'string') {
          return item.trim();
        }
        if (item && typeof item === 'object') {
          const code = 'code' in item && typeof item.code === 'string' ? item.code : '';
          const message = 'message' in item && typeof item.message === 'string' ? item.message : '';
          return [code, message].filter(Boolean).join(': ');
        }
        return '';
      })
      .filter(Boolean)
      .join('\n');
  }
  return '';
}

export function formatVoucherPreviewErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    const headline = error.message?.trim() || 'Preview request failed';
    const statusNote = typeof error.status === 'number' ? ` (HTTP ${error.status})` : '';
    const details = formatApiErrorDetails(error.errors);
    return details ? `${headline}${statusNote}\n${details}` : `${headline}${statusNote}`;
  }
  if (error instanceof Error) {
    return error.message.trim() || 'Preview request failed';
  }
  return 'Preview request failed';
}

function splitPreviewErrorMessage(message: string) {
  const lines = message
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

  return {
    previewErrorHeadline: lines[0] ?? '',
    previewErrorDetailBlock: lines.slice(1).join('\n').trim(),
  };
}

export function useVoucherPreview({
  currencyOptions,
  defaultCurrencyCode,
  defaultLocale,
  buildPayload,
  getMissingFieldMessages,
}: UseVoucherPreviewParams) {
  const {
    data: previewData,
    error: previewError,
    isError: isPreviewError,
    isPending: isPreviewPending,
    mutateAsync: previewVoucher,
    reset: resetPreview,
  } = usePreviewVoucher();

  const resolvedCurrencyOptions = useMemo(
    () => getPreviewCurrencyOptions(currencyOptions, defaultCurrencyCode),
    [currencyOptions, defaultCurrencyCode],
  );
  const [previewCurrencyCode, setPreviewCurrencyCode] = useState(defaultCurrencyCode);
  const [previewLocale, setPreviewLocale] = useState(defaultLocale);
  const [previewLocalError, setPreviewLocalError] = useState('');

  useEffect(() => {
    const nextCurrency = defaultCurrencyCode.trim().toUpperCase() || resolvedCurrencyOptions[0] || 'AUD';
    setPreviewCurrencyCode((currentCurrency) =>
      resolvedCurrencyOptions.includes(currentCurrency) ? currentCurrency : nextCurrency,
    );
  }, [defaultCurrencyCode, resolvedCurrencyOptions]);

  useEffect(() => {
    setPreviewLocale(defaultLocale);
  }, [defaultLocale]);

  const handlePreview = useCallback(async () => {
    setPreviewLocalError('');

    const missingFieldMessages = getMissingFieldMessages?.() ?? [];
    if (missingFieldMessages.length > 0) {
      resetPreview();
      setPreviewLocalError(
        `Fill in the required voucher fields before previewing: ${missingFieldMessages.join('; ')}.`,
      );
      return;
    }

    const payload = buildPayload();
    if (!payload) {
      resetPreview();
      setPreviewLocalError('Fill in the required voucher fields before previewing.');
      return;
    }

    try {
      await previewVoucher({
        ...payload,
        previewCurrencyCode,
        locale: previewLocale,
      });
    } catch {
      // Rendered from mutation state.
    }
  }, [buildPayload, getMissingFieldMessages, previewCurrencyCode, previewLocale, previewVoucher, resetPreview]);

  const previewErrorMessage = isPreviewError ? formatVoucherPreviewErrorMessage(previewError) : '';
  const { previewErrorHeadline, previewErrorDetailBlock } = splitPreviewErrorMessage(previewErrorMessage);
  const publicPreviewVoucher: PreviewPublicVoucher | undefined = previewData?.result?.publicVoucher;
  const previewTextReplacements: Array<PreviewVoucherTextReplacement> = previewData?.result?.textReplacements ?? [];

  return {
    handlePreview,
    isPreviewError,
    isPreviewPending,
    previewCurrencyCode,
    previewLocale,
    previewErrorDetailBlock,
    previewErrorHeadline,
    previewLocalError,
    previewTextReplacements,
    publicPreviewVoucher,
    resolvedCurrencyOptions,
    setPreviewCurrencyCode,
    setPreviewLocale,
  };
}
